/**
 * @fileoverview Zod schemas for the Discover saved-search backend (FRE-926).
 *
 * The create endpoint accepts `{ label, queryState }`. `queryState` is the
 * canonical serialized Discover URL state (`serializeDiscoverParams` output) —
 * stored verbatim so a re-run reuses the single-fetch cold-load cache key. We
 * validate it is a non-empty, bounded query-string-shaped value but do NOT
 * re-parse/normalize it here: the canonical form is owned by the web
 * serializer, and the backend must round-trip it byte-for-byte.
 *
 * @since FRE-926 (Discover personalization foundation — saved-search backend)
 */

import { z } from 'zod';

/** Max length for a saved-search label (after trim). */
export const SAVED_SEARCH_LABEL_MAX_LENGTH = 120;

/**
 * Max length for the serialized `queryState`. Discover URL state is small
 * (a handful of short filter keys); 2048 is a generous bound that rejects
 * obviously abusive payloads without truncating any legitimate filter set.
 */
export const SAVED_SEARCH_QUERY_STATE_MAX_LENGTH = 2048;

/**
 * Per-user cap on saved searches. Beyond this, create returns
 * `409 SAVED_SEARCH_LIMIT_REACHED`. A small cap keeps the rail scannable and
 * bounds per-user storage; the user deletes one to make room.
 */
export const SAVED_SEARCH_PER_USER_LIMIT = 50;

/**
 * Body schema for `POST /api/v1/users/me/saved-searches`.
 *
 * - `label` is trimmed and bounded; empty-after-trim is rejected.
 * - `queryState` is trimmed and bounded. We reject a leading `?` so the stored
 *   value is exactly the `URLSearchParams.toString()` canonical form (no
 *   leading separator), matching what the web serializer emits.
 */
export const createSavedSearchSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, 'label is required')
    .max(
      SAVED_SEARCH_LABEL_MAX_LENGTH,
      `label must be at most ${SAVED_SEARCH_LABEL_MAX_LENGTH} characters`,
    ),
  queryState: z
    .string()
    .trim()
    .min(1, 'queryState is required')
    .max(
      SAVED_SEARCH_QUERY_STATE_MAX_LENGTH,
      `queryState must be at most ${SAVED_SEARCH_QUERY_STATE_MAX_LENGTH} characters`,
    )
    .refine((value) => !value.startsWith('?'), {
      message: 'queryState must be the canonical query string without a leading "?"',
    }),
});

export type CreateSavedSearchInput = z.infer<typeof createSavedSearchSchema>;
